import { useMemo, type ReactElement } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  Button,
  EmptyState,
  FilterSheet,
  Input,
  MultiCombobox,
  Panel,
  PanelHeader,
  SegmentedControl,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { DashboardFilterBar, FilterSlot } from '@/components/dashboards/DashboardFilterBar';
import { pluralise } from '@/lib/format';
import { CONVERSION_APPROXIMATE_NOTE } from '@/lib/targets';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import {
  DEFAULT_REVENUE_CURRENCY,
  REVENUE_CURRENCIES,
  todayIso,
  useRevenueAnalytics,
  type MoverDirection,
  type RevenueCurrency,
} from '@/lib/use-revenue-analytics';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { TopMoversSummary } from './TopMoversSummary';
import { TopMoversTable } from './TopMoversTable';
import {
  DIRECTION_META,
  DIRECTION_ORDER,
  EMPTY_MOVER_FILTERS,
  applyMoverFilters,
  asMoverDirection,
  buildMoverRows,
  compactMoneyText,
  comparisonPairOf,
  countActiveAdvanced,
  cycleColumnKey,
  cycleProgress,
  cycleSortKey,
  formatPercentChange,
  groupByDirection,
  moverColumnKeys,
  moverOrderFrom,
  partialCycleNote,
  regionKeyOf,
  regionLabelsFrom,
  resolveSelectedCycles,
  summariseDirections,
  type MoverFilters,
  type MoverRow,
  type MoverSort,
} from './top-movers';

/**
 * **TOP MOVERS** — which companies are buying more year on year, which are
 * buying less, which have started, and which have STOPPED.
 *
 * The owner's brief, and *"every scenario"* is the requirement rather than a
 * flourish: *"top movers will be based on company — which company is year on
 * year purchasing more products will be at the top, and at the bottom also
 * which are not purchasing, and also the companies which are buying but have
 * stopped. I want every scenario in this tab."*
 *
 * ── 🔴 THE CYCLES ARE A MULTI-SELECT, AND THE WARNING CAME BACK WITH IT ─────
 *
 * Owner's instruction, 11 September 2026: *"on top movers the cycles fixed to
 * 2024-2025, 2025-2026 i want a swticher to add more cycles to the table you
 * have removed this thing add the cycle switcher there."*
 *
 * ⚠️ **THIS PARTLY REVERSES A DECISION TAKEN THREE HOURS EARLIER THE SAME
 * DAY.** *SUPERSEDED: "THE COMPARISON IS FIXED: THE TWO MOST RECENT COMPLETE
 * CYCLES … There is no cycle picker on this tab and there is no partial-cycle
 * warning either — the second follows from the first … Fixing the basis makes
 * the trap unreachable rather than explained, which is the only kind of warning
 * removal that is safe. Anything that re-introduces a cycle picker re-introduces
 * the trap and must bring the callout back with it."* **That last sentence is
 * the specification for this change, and it has been honoured** — see
 * `partialCycleNote` and the warning strip below the panel header.
 *
 * Three things follow, and none of them is optional:
 *
 * 1. **THE DEFAULT IS UNCHANGED: the two most recent COMPLETE cycles.** A
 *    reader who touches nothing sees exactly what they saw before the picker
 *    existed, and therefore no warning at all. `defaultCycleSelection` is
 *    `latestCompletePair` and nothing else.
 * 2. 🔴 **CHANGE, % CHANGE AND ALL FIVE OUTCOMES COME FROM THE NEWEST TWO
 *    SELECTED, AND THE SUBHEADING NAMES THEM.** With three or more columns a
 *    reader cannot tell which pair the Change column is about and will assume
 *    newest-versus-oldest — a wrong number wearing a plausible label, which is
 *    precisely the failure the fixed basis was introduced to prevent. The
 *    columns say which cycles are SHOWN; only that sentence says which two were
 *    SUBTRACTED.
 * 3. 🔴 **A SELECTED CYCLE THAT HAS NOT FINISHED WARNS, IN ONE LINE.** Measured
 *    on production: the running `2026 - 27` against a complete cycle gives **22
 *    Declining and 35 Stopped**, where two complete cycles over the same data
 *    give **11 and 12**. Twenty-three companies read as lost customers purely
 *    because they have not been invoiced yet. **Not the old amber panel** — that
 *    carried three paragraphs and a button that changed the comparison for you,
 *    and the button has no job now that the reader has a picker.
 *
 * ⚠️ **FEWER THAN TWO SELECTED IS A REAL STATE AND GETS AN EXPLICIT EMPTY
 * STATE, WITH THE PICKER STILL ON SCREEN.** A comparison against nothing makes
 * every company read as `New`. The empty state replaces the TILES AND THE TABLE
 * and never the filter bar: a control that can put the screen into a state must
 * survive that state, or the reader is stuck in it — the same rule that keeps
 * this table mounted when a column funnel matches nothing.
 *
 * ── 🔴 THE CURRENCY CONVERTS. IT IS A BASE, NOT A FILTER — owner, 2026-09-18 ─
 *
 * ⚠️ *SUPERSEDED: "**ONE CURRENCY AT A TIME**, by decision … this tab was
 * deliberately left filtering, so the lie below is unchanged. A company that
 * changed billing currency between the two cycles reads as `stopped` under one
 * and `new` under the other — one ordinary change, two false findings. Every such
 * row carries the caveat, and so does the tile that counts it."*
 *
 * `GET /revenue-tracker` takes a `baseCurrency` and prices every paid line at
 * **its own invoice's `bill_date`**, so this control asks *"show me the movement
 * in pounds"* rather than *"show me the pound part of the movement"*.
 *
 * ✅ **THAT CLOSES THE SHARPEST TRAP THIS TAB HAD.** A company that switched from
 * dollars to euros is no longer `stopped` under one and `new` under the other:
 * both currencies are summed into the base and the movement is the movement.
 * `currencySwitchHint` and `otherCurrenciesEver` answer nothing on a converted
 * response of their own accord — the data genuinely no longer holds a second
 * currency — and they return, correctly, against a server that does not convert.
 *
 * 🔴 **AND IT OPENS A DIFFERENT ONE, WHICH THIS TAB ALONE HAS TO SAY OUT LOUD:
 * PART OF A MOVEMENT CAN NOW BE THE EXCHANGE RATE RATHER THAN THE CUSTOMER.**
 * Each cycle is priced at its own documents' bill dates, so a company billed in
 * dollars whose dollar revenue did not move shows a change in pounds whenever the
 * rate moved between the two. **652 of 797 invoices are USD**, an ordinary
 * year-on-year rate move is a few per cent, and it can flip the DIRECTION — three
 * per cent more in dollars against a five per cent adverse rate reads as `down`.
 * ⛔ **It cannot be corrected here**: an FX-neutral change needs the native
 * amounts and the per-line rates, neither of which reaches the browser, and
 * re-deriving one from a single rate would be client-side arithmetic across
 * units. **So it is stated, in one line under the comparison heading, gated on
 * the server's echo.** See that sentence for why it is there rather than in the
 * warning strip below it.
 *
 * ── THE TWO THINGS THAT WOULD STILL MAKE THIS TAB LIE ───────────────────────
 *
 * 1. **A MOVEMENT IS NOT ALL THE CUSTOMER'S** — above.
 * 2. **FOUR OF THE FIVE OUTCOMES ARE NOT A PERCENTAGE**, and none is invented.
 *
 * ── THE LAYOUT, LARGEST QUESTION FIRST ─────────────────────────────────────
 *
 *     the filter bar      which currency, and which cycles are columns — plus
 *                         one threshold behind a disclosure, because it is the
 *                         only narrowing a column header cannot own
 *     five outcome tiles  how many, worth how much, in each of the five
 *     the sectioned table who moved and by how much, filtered and sorted from
 *                         its own COLUMN HEADERS
 *
 * ── 🔴 FILTERING AND SORTING LIVE ON THE COLUMNS ───────────────────────────
 *
 * Owner's instruction, same day: the same column-level filtering the companies
 * list has. So `useTableFilters` owns the state, `FilterableTH` draws the
 * funnels, and the controls that used to sit in the bar — company search,
 * billing region, outcome, and a grouped *Order by* select — are gone from it.
 *
 * ⚠️ **A SORT IS OFFERED ONLY BY A COLUMN THAT DECLARES A `sortKey`.** That is
 * not a convention here, it is the mechanism: `headerFor(key).filter.sort` is
 * `undefined` otherwise, so an unbacked sort cannot be rendered at all. The
 * alternative — a hand-written list of orderings — is what put two dead options
 * on `/companies` for a week, refusing every click with no error and no
 * request. Every `sortKey` below is typed against `MoverSort`, so a typo is a
 * BUILD failure rather than a silent one.
 *
 * ⚠️ **THE TILES AND THE OUTCOME FUNNEL ARE ONE STATE, NOT TWO.** The tiles
 * read and write `headerFor('outcome').filter`, so pressing a tile fills that
 * column's funnel and clearing the funnel releases the tile.
 *
 * ── WHAT THIS COMPONENT OWNS, AND WHAT IT DOES NOT ─────────────────────────
 *
 * ⚠️ **NO `PageHeader` AND NO PERMISSION CHECK.** The page owns the title, the
 * tab strip and the gate; a second permission check here would be a second
 * answer to one question, and a second `PageHeader` would render two titles.
 *
 * ⚠️ **FILTER STATE IS LOCAL, NOT IN THE URL.** Four panels share this page's
 * query string, and a generic key here (`q`, `region`) would be read by a
 * sibling tab as its own. The cost is stated rather than hidden: **a filtered
 * view of this tab is not shareable by link.**
 */

const CURRENCY_OPTIONS = REVENUE_CURRENCIES.map((code) => ({ value: code, label: code }));

/**
 * The outcome column's option list, written out rather than accumulated.
 *
 * ⚠️ **DECLARED, SO ALL FIVE ARE OFFERED EVEN WHEN NOBODY LANDED IN ONE.**
 * `useTableFilters` otherwise builds a page column's options from the values it
 * can SEE, and an outcome with no companies would then be missing from its own
 * filter — the reader could not tell *"nobody stopped"* from *"this filter does
 * not know about Stopped"*. It is the same reason the tiles all render.
 *
 * ⚠️ **IN `DIRECTION_ORDER`, AND `ColumnSpec.options` DELIBERATELY DOES NOT
 * ALPHABETISE.** That order is the owner's reading order — growing at the top,
 * gone at the bottom — and sorting it by label would put *Declining* first and
 * destroy it while looking tidier.
 */
const OUTCOME_OPTIONS = DIRECTION_ORDER.map((direction) => ({
  value: direction,
  label: DIRECTION_META[direction].label,
}));

export function TopMoversPanel(): ReactElement {
  /*
   * Layout manager (owner 2026-10-20): the outcome tiles and the comparison
   * table are movable per person. Both exist only while two cycles are being
   * compared; the empty state that replaces them stays outside the grid. The
   * ids are stored per user — never rename them. Called before the early
   * returns below, so the hook order never changes.
   */
  const layout = usePageLayout('management-dashboard.movers', [
    { id: 'summary', label: 'Outcomes' },
    { id: 'movers', label: 'Movers' },
  ]);
  const [currency, setCurrency] = useRememberedState<RevenueCurrency>(
    'top-movers-currency',
    DEFAULT_REVENUE_CURRENCY,
    (raw) =>
      typeof raw === 'string' && (REVENUE_CURRENCIES as readonly string[]).includes(raw)
        ? (raw as RevenueCurrency)
        : undefined,
  );
  const [filters, setFilters] = useRememberedState<MoverFilters>(
    'top-movers-filters',
    EMPTY_MOVER_FILTERS,
    (raw) =>
      typeof raw === 'object' &&
      raw !== null &&
      typeof (raw as { minRevenue?: unknown }).minRevenue === 'string'
        ? { minRevenue: (raw as { minRevenue: string }).minRevenue }
        : undefined,
  );

  /*
   * 🔴 **THE CURRENCY IS A REQUEST PARAMETER NOW, so the read is keyed on it.**
   * It converts rather than narrowing, and the arithmetic belongs to the server:
   * only it holds the rates and each line's own bill date.
   */
  const { rows, cycles, loading, error, unmatchedLines, convertedTo, unpricedLines, reload } =
    useRevenueAnalytics(currency);

  /*
   * 🔴 **THE UNIT EVERY FIGURE ON THIS TAB IS IN, RESOLVED ONCE AND READ FROM THE
   * SERVER'S ECHO.** An API predating `baseCurrency` ignores the key and answers
   * a perfectly ordinary `200` of UNCONVERTED rows; a panel labelling those from
   * `currency` would print `GBP` over dollars, and on this tab it would also
   * subtract one unit from another and call the difference a movement.
   *
   * ⚠️ **Falling back to `currency` restores the OLD behaviour exactly** — filter
   * and all, including the currency-switch caveats, which is the honest
   * degradation.
   *
   * ⚠️ **NARROWED TO A CURRENCY THIS TAB CAN RENDER.** Every render prop here
   * takes {@link RevenueCurrency}; casting an unrecognised echo through would put
   * a code on screen the page cannot format, and falling back to the picker would
   * label the response with a unit it is not in. Unreachable against a conforming
   * server, which can only echo a code it was sent.
   */
  const measuredIn =
    convertedTo !== null && (REVENUE_CURRENCIES as readonly string[]).includes(convertedTo)
      ? (convertedTo as RevenueCurrency)
      : null;
  const converted = measuredIn !== null;
  const figuresCurrency: RevenueCurrency = measuredIn ?? currency;

  /**
   * The cycles the reader has ticked, or `null` while they have ticked nothing.
   *
   * 🔴 **`null` IS A THIRD STATE, NOT A SPELLING OF "NONE SELECTED".** It is
   * what lets the default be DERIVED during render rather than written into
   * state by an effect — and an effect would render the empty selection once
   * before correcting it, which is the frame somebody screenshots. It is also
   * what makes the default follow the data: the cycle list arrives after the
   * first render, so state initialised at mount would be empty for ever.
   *
   * ⚠️ **IDS, NOT NAMES.** A cycle's name is admin-editable; the id is the key
   * the rest of this app matches on. ⚠️ **And they are never ORDERED by** —
   * `resolveSelectedCycles` filters the date-ordered list instead, because id 11
   * is 2022-23 and id 1 is 2025-26.
   */
  const [chosenCycleIds, setChosenCycleIds] = useRememberedState<readonly number[] | null>(
    'top-movers-cycles',
    null,
    (raw) =>
      raw === null || (Array.isArray(raw) && raw.every((id) => typeof id === 'number'))
        ? (raw as readonly number[] | null)
        : undefined,
  );

  const today = todayIso();

  /*
   * The cycle COLUMNS, oldest first. Everything below is a function of this —
   * the specs, the headers, the mobile sheet, the comparison pair and the
   * warning — so there is exactly one answer to "which cycles is this tab
   * about" and nothing can hold a second one.
   */
  const selectedCycles = useMemo(
    () => resolveSelectedCycles(cycles, chosenCycleIds, today),
    [cycles, chosenCycleIds, today],
  );

  /*
   * 🔴 **THE COMPARISON IS THE NEWEST TWO SELECTED, AND THE SUBHEADING SAYS
   * SO.** Never the first and last of the selection: with four columns that
   * would compare 2022-23 against 2025-26 while the Change column sat beside a
   * 2024-25 column, and every figure on screen would be real.
   */
  const { current, previous } = useMemo(() => comparisonPairOf(selectedCycles), [selectedCycles]);

  /*
   * 🔴 **THE WARNING THE PICKER BRINGS BACK.** `null` — nothing on screen —
   * while every selected cycle has finished, which is the default view. See
   * `partialCycleNote` for the measurement that makes it load-bearing.
   */
  const cycleNote = useMemo(() => partialCycleNote(selectedCycles, today), [selectedCycles, today]);

  /*
   * The picker's options. Every catalogued cycle is offered — including the one
   * being traded in and the ones catalogued ahead — because the reader is
   * allowed to ask that question; the warning is what stops the answer being
   * misread.
   *
   * ⚠️ **THE HINT SAYS WHICH ONES HAVE NOT FINISHED, BEFORE THEY ARE PICKED.**
   * A caption that only appears after the fact makes the reader undo something;
   * this lets them see it coming. A complete cycle carries no hint at all —
   * labelling the ordinary case is noise that teaches people to skip the line
   * that matters.
   */
  const cycleOptions = useMemo(
    () =>
      cycles.map((cycle) => {
        const progress = cycleProgress(cycle, today);
        if (progress?.complete === true) return { value: String(cycle.id), label: cycle.name };
        if (progress === null) {
          return { value: String(cycle.id), label: cycle.name, hint: 'Has not finished' };
        }
        return {
          value: String(cycle.id),
          label: cycle.name,
          hint: progress.running
            ? `Still running — ${progress.elapsedDays} of ${progress.totalDays} days elapsed`
            : 'Has not started',
        };
      }),
    [cycles, today],
  );

  /*
   * ⚠️ **MAPPED BACK THROUGH THE REAL CYCLE LIST, NEVER PARSED.**
   * `values.map(Number)` would turn anything unexpected into `NaN` and store it
   * as a selection nothing can ever match — a column that silently fails to
   * appear. Filtering the list also fixes the ORDER at the point of writing, so
   * the stored ids are chronological whatever order the boxes were ticked in.
   */
  const onCyclesChange = (values: string[]) => {
    const ticked = new Set(values);
    setChosenCycleIds(
      cycles.filter((cycle) => ticked.has(String(cycle.id))).map((cycle) => cycle.id),
    );
  };

  /* Every company with a movement in this unit, before any filter narrows it.
   *
   * ⚠️ **`figuresCurrency`, NEVER `currency`, from here down.** Everything below
   * reaches the wire through `revenueFor`, whose parameter is the unit the
   * FIGURES are in — the server's echo where it converted, the picker's value
   * where it did not. Passing the request instead is the one mistake that
   * mislabels money, and it typechecks. */
  const allMovers = useMemo(
    () => buildMoverRows(rows, selectedCycles, figuresCurrency),
    [rows, selectedCycles, figuresCurrency],
  );

  const regionLabels = useMemo(() => regionLabelsFrom(allMovers), [allMovers]);

  /*
   * ⚠️ **EVERY COLUMN IS `scope: 'page'`, AND THAT IS HONEST HERE RATHER THAN A
   * COMPROMISE.** On every other table in this app a page-scoped filter touches
   * twenty of several thousand server-paginated rows. This tab has already
   * fetched the WHOLE cross-tab — `useRevenueAnalytics` follows the pager to the
   * last page — so "the rows loaded here" and "every company" are the same set.
   *
   * ⚠️ **`sortKey` IS TYPED `MoverSort`, WHICH IS THE WHOLE GUARD.** The only
   * route into the sort state is a column declaring one, and `moverOrderFrom`
   * is the only reader — so a scale that no column claims cannot be offered and
   * a key no comparator handles cannot be written.
   */
  const columns = useMemo<ColumnSpec<MoverRow>[]>(() => {
    const sortKey = (key: MoverSort) => key;
    return [
      {
        key: 'company',
        label: 'Company',
        get: (row) => row.mover.row.company.name,
        sortKey: sortKey('company'),
      },
      {
        /*
         * ⚠️ **VALUED BY `slug`, LABELLED BY `name`.** A region's display name
         * is editable; its slug is the join key the rest of this app matches
         * on. A company with none is genuinely EMPTY rather than a sentinel
         * option, so the popover's own *Is empty* is how a reader reaches the
         * nineteen companies with no country of record.
         */
        key: 'region',
        label: 'Billing region',
        get: (row) => regionKeyOf(row.mover.row),
        format: (slug) => regionLabels[slug] ?? slug,
        sortKey: sortKey('region'),
      },
      {
        key: 'outcome',
        label: 'Outcome',
        get: (row) => row.mover.direction,
        options: OUTCOME_OPTIONS,
        /*
         * ⚠️ **NO `sortKey`, DELIBERATELY.** The table is already GROUPED by
         * outcome into five banded sections, so an ordering by outcome would be
         * a control with nothing to do — and `useTableFilters` renders no sort
         * UI at all for a column that declares none, which is exactly right.
         */
      },
      /*
       * 🔴 **ONE COLUMN PER SELECTED CYCLE, GENERATED FROM THE SAME ARRAY THE
       * TABLE DRAWS ITS HEADERS FROM.** The key comes from `cycleColumnKey` on
       * both sides, so a column cannot exist in one and not the other —
       * `headerFor` answers an unrecognised key with a plain header rather than
       * an error, which is how a raw `cycle:11` ends up as a heading with no
       * funnel under it and nothing failing.
       *
       * ⚠️ **ONLY THE LAST TWO DECLARE A `sortKey`, AND THAT IS THE WHOLE
       * ANSWER TO THE DYNAMIC-SORT PROBLEM.** `MoverSort` stays a CLOSED union
       * of six literals, so every key a header can offer is one the comparator
       * handles — checked by `tsc`, not at run time. A generated `cycle:<id>`
       * sort key would typecheck while naming a cycle nobody selected, which is
       * a control that silently refuses every click. The full argument, and the
       * cost (a context column filters but does not sort), is on `MoverSort`.
       *
       * ⚠️ **The label is the cycle's own name**, so the heading names the year
       * and the table cannot disagree with the subheading about which cycles
       * are on screen. `Is empty` is a real question on every one of them: no
       * revenue in that cycle, in this currency.
       */
      ...selectedCycles.map((cycle, index): ColumnSpec<MoverRow> => {
        const fromEnd = selectedCycles.length - 1 - index;
        return {
          key: cycleColumnKey(cycle),
          label: cycle.name,
          get: (row) => row.amounts[cycle.name] ?? null,
          /*
           * ⚠️ **THE FUNNEL IS FORMATTED EXACTLY AS THE COLUMN IS**, which is
           * the same ruling the `% change` column below makes for the same
           * reason: an option reading `1,200,000.00` beside a cell reading
           * `£1.20M` is an option a reader cannot match to the row it selects.
           * The option's VALUE stays the exact decimal, so nothing is
           * mis-filtered — see `compactMoneyText` for what compaction costs a
           * list of options and why it is accepted.
           */
          format: (value) => compactMoneyText(value, figuresCurrency),
          /*
           * ✅ **EVERY CYCLE COLUMN SORTS — owner's decision, 2026-09-11.**
           *
           * *SUPERSEDED: "ONLY THE LAST TWO DECLARE A `sortKey` … a context
           * column filters but does not sort."* That shipped, and an
           * independent check measured what it cost: three money columns
           * rendering identically, two offering a sort and one not, with
           * FILTERING offered on all three — so no rule was inferable from the
           * screen.
           *
           * The compared pair keeps its FIXED keys, so the two scales the
           * sections are ranked against stay compiler-checked members of
           * `MoverSort`. Every other cycle gets `cycleSortKey`, resolved against
           * the live selection by `moverOrderFrom`.
           *
           * ⚠️ **The old objection was right about the hazard and wrong about
           * the only cure.** A generated key CAN name a cycle nobody selected —
           * so it is RESOLVED rather than trusted, and an unresolvable one falls
           * back to the default ranking. What makes that honest is that the only
           * way to strand such a key is to deselect the cycle, which removes the
           * column: the reader sees the cause and the effect in one gesture.
           */
          sortKey:
            fromEnd === 0
              ? sortKey('current')
              : fromEnd === 1
                ? sortKey('previous')
                : cycleSortKey(cycle),
          numeric: true,
        };
      }),
      {
        /*
         * ⚠️ **SORTED BY `movement`, WHICH IS NOT ALWAYS THIS CELL.** Each
         * section is ranked by the figure it actually HAS: `up` and `down` by
         * the size of the change, `stopped` by the revenue lost, `new` by the
         * revenue won. Ranking every section by this column's own value would
         * order `stopped` and `new` arbitrarily — both are `null` there by
         * design — and the largest lost account would sit wherever the row
         * order happened to put it.
         */
        key: 'change',
        label: 'Change',
        get: (row) => row.mover.change,
        /*
         * ⚠️ **A NEGATIVE OPTION KEEPS ITS SIGN.** This is the one funnel on the
         * tab listing signed money, and an option that lost its `-` would offer
         * a fall as though it were a rise — the same defect as the cell, in the
         * control that selects it. No `+` is added here: a funnel lists values,
         * and the explicit `+` belongs to a `up` ROW rather than to the number.
         */
        format: (value) => compactMoneyText(value, figuresCurrency),
        sortKey: sortKey('movement'),
        numeric: true,
      },
      {
        /*
         * ⚠️ **The cell's value is the FORMATTED percentage**, so the funnel
         * lists what the column shows. `Is empty` selects exactly the rows that
         * have no percentage — `new`, `stopped`, and anything whose earlier
         * cycle was exactly 0.00 — which is a question worth being able to ask.
         */
        key: 'percent',
        label: '% change',
        get: (row) => (row.mover.percent === null ? null : formatPercentChange(row.mover.percent)),
        sortKey: sortKey('percent'),
        numeric: true,
      },
    ];
    /*
     * 🔴 **`figuresCurrency` IS A DEPENDENCY BECAUSE THE MONEY FORMATTERS CLOSE
     * OVER IT.** Without it the funnels keep rendering the PREVIOUS unit's symbol
     * after a switch — `£1.20M` over a column of dollars — which is a wrong label
     * on a correct figure and nothing anywhere fails.
     */
  }, [selectedCycles, regionLabels, figuresCurrency]);

  /*
   * Every column key in table order — the `colSpan` on the section bands, the
   * empty row, and the sections of the mobile filter sheet. Built from the same
   * `selectedCycles` the specs above are, and PASSED to the table rather than
   * recomputed there.
   */
  const columnKeys = useMemo(() => moverColumnKeys(selectedCycles), [selectedCycles]);

  // A stable memory id: the cycle columns arrive after /cycles loads, so a key
  // hashed from the column set would orphan the remembered sort.
  const tableFilters = useTableFilters(allMovers, columns, { memoryId: 'top-movers' });
  const { headerFor, activeCount, clearAll } = tableFilters;

  /*
   * ⚠️ **A FUNNEL SET ON A CYCLE COLUMN THAT IS THEN DESELECTED STOPS APPLYING
   * AND COMES BACK IF THE CYCLE IS RE-SELECTED.** `useTableFilters` keys its
   * state by column key and only ever consults the columns it was handed, so a
   * deselected cycle's filter is inert rather than lost — and inert is right,
   * because a column nobody can see must not be narrowing the table. It is not
   * silent when it returns: the funnel renders filled and `activeCount` counts
   * it, which is what the caption's "N of M companies shown" is built on.
   */

  /*
   * ⚠️ **THE ORDER OF THE TWO FILTERING STEPS IS DELIBERATE.** The hook sees
   * `allMovers`, so every funnel's option list covers the whole comparison
   * rather than whatever the threshold happened to leave. The threshold then
   * narrows what the hook returned.
   */
  const visible = useMemo(
    () => applyMoverFilters(tableFilters.visibleRows, filters),
    [tableFilters.visibleRows, filters],
  );

  /*
   * ⚠️ **THE TILES COUNT THE UNFILTERED SET, DELIBERATELY.** They are the tab's
   * map of what exists — narrowing to `Stopped` and watching the other four
   * tiles drop to nothing would destroy the one thing they are for, and a
   * reader who had filtered would then believe nobody was growing.
   */
  const summaries = useMemo(() => summariseDirections(allMovers), [allMovers]);
  /*
   * ⚠️ **`selectedCycles` IS PASSED SO A CYCLE KEY CAN BE RESOLVED**, and the
   * memo depends on it: deselecting the cycle the table is sorted by must
   * recompute the order, or the table would go on ranking by a column that is
   * no longer drawn.
   */
  const order = useMemo(
    () => moverOrderFrom(tableFilters.sort, selectedCycles),
    [tableFilters.sort, selectedCycles],
  );
  const sections = useMemo(() => groupByDirection(visible, order), [visible, order]);

  /*
   * 🔴 **THE TILES DRIVE THE OUTCOME COLUMN'S OWN FUNNEL.** One state, two
   * controls — a tile that remembered its own selection would disagree with the
   * header the first time somebody used the other one, and a reader would have
   * two things on screen claiming different filters.
   */
  const outcomeFilter = headerFor('outcome').filter;
  const outcomeSelected = outcomeFilter?.value.selected ?? [];
  /*
   * ⚠️ **A tile lights up only for a SINGLE selection.** The funnel is
   * multi-select and two ticked outcomes are not a state any one tile can
   * represent — showing both pressed would claim a filter nobody set.
   */
  const activeOutcome: MoverDirection | 'all' =
    outcomeSelected.length === 1 ? (asMoverDirection(outcomeSelected[0]) ?? 'all') : 'all';

  const selectOutcome = (direction: MoverDirection | 'all') => {
    if (!outcomeFilter) return;
    outcomeFilter.onChange({
      ...outcomeFilter.value,
      selected: direction === 'all' ? [] : [direction],
    });
  };

  const advancedCount = countActiveAdvanced(filters);
  /* Anything NARROWING the table, whichever control set it. */
  const narrowed = advancedCount > 0 || activeCount > 0;

  const clearEverything = () => {
    setFilters(EMPTY_MOVER_FILTERS);
    clearAll();
  };

  /* ---------------- loading and failure ---------------- */

  if (loading) {
    return (
      <Panel className="min-w-0">
        <PanelHeader title="Top Movers" description="Reading the revenue tracker…" />
        <div className="flex flex-col gap-2 p-4">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <Skeleton key={n} className="h-9 w-full" />
          ))}
        </div>
      </Panel>
    );
  }

  if (error !== null) {
    return (
      <Panel className="min-w-0">
        <EmptyState
          title="The revenue figures could not be loaded"
          /* The server's own sentence, not a rewrite of it. */
          description={error}
          action={
            <Button variant="secondary" onClick={reload}>
              Try again
            </Button>
          }
        />
      </Panel>
    );
  }

  /*
   * ⚠️ **ONE LINE NAMING THE UNIT AND THE POPULATION, under every heading on
   * this tab.** *"Revenue"* alone invites a figure here to be quoted as the
   * business's revenue, and it is not: it is paid invoices, including tax
   * (owner, 2026-10-16; it was before tax), in ONE currency, over two named
   * cycles.
   *
   * ⚠️ **"either COMPARED cycle", and the word is load-bearing once a third
   * column can exist.** A company that traded only in a context cycle has no
   * movement between the compared two, so it is not listed at all — see
   * `buildMoverRows`. Without the word, a reader who added 2022-23 and found no
   * row for a company that traded only in 2022-23 would read it as missing data.
   */
  const caption = [
    'Paid invoices, incl. tax',
    /* ⚠️ The unit the figures are IN — the server's echo, never the request. */
    figuresCurrency,
    narrowed
      ? `${visible.length} of ${allMovers.length} companies shown`
      : `${pluralise(allMovers.length, 'company', 'companies')} with revenue in either compared cycle`,
  ].join(' · ');

  /*
   * 🔴 **FEWER THAN TWO CYCLES TO COMPARE — THREE CAUSES, THREE SENTENCES.**
   * A comparison against nothing makes every company read as `New`, so nothing
   * is rendered for it. The three are told apart because they send the reader
   * to different places: two of them are the catalogue's problem and one is a
   * box they can tick right now.
   */
  const noComparisonTitle =
    cycles.length < 2 ? 'There are not two cycles to compare' : 'Choose at least two cycles';

  const noComparisonDescription =
    cycles.length === 0
      ? 'No cycles are catalogued, so there are no periods to measure movement between.'
      : cycles.length === 1
        ? `Only ${cycles[0]?.name ?? 'one cycle'} is catalogued. Movement is measured between two cycles, so there is nothing to measure it against.`
        : [
            'Movement is measured between two cycles — the later one against the one immediately before it — so a single cycle has nothing to compare against.',
            chosenCycleIds === null
              ? /*
                 * ⚠️ **WHY NOTHING WAS SELECTED FOR THEM.** Silence here reads
                 * as a broken tab. The tab opens on two COMPLETE cycles and will
                 * not quietly substitute a running one, so when fewer than two
                 * have ended the honest answer is to say so and let them choose.
                 */
                'Top Movers opens on the two most recent cycles that have both FINISHED, and fewer than two have — and it will not default to one still being traded in, because that makes most companies read as declining.'
              : selectedCycles.length === 1
                ? `Only ${selectedCycles[0]?.name} is selected.`
                : 'None is selected.',
            'Pick them under Cycles, above.',
          ].join(' ');

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <DashboardFilterBar
        rememberAs="top-movers-more-filters"
        /*
         * 🔴 **BOTH PRIMARY CONTROLS CHANGE WHAT THE NUMBERS MEAN, WHICH IS
         * WHAT PUTS THEM HERE RATHER THAN BEHIND THE DISCLOSURE.** Currency
         * decides the UNIT of every figure — a reader who cannot see it is one
         * who quotes dollars as pounds — and the cycles decide the PERIOD, and
         * with it which two years the Change column subtracts.
         */
        primary={
          <>
            <FilterSlot label="Currency">
              <SegmentedControl
                label="Currency"
                size="sm"
                value={currency}
                options={CURRENCY_OPTIONS}
                onChange={setCurrency}
              />
            </FilterSlot>

            {/*
              🔴 **THE CYCLE SWITCHER, AND IT IS ALWAYS RENDERED.** It sits
              outside the comparison branch below deliberately: selecting one
              cycle is a state this control can put the screen into, so it has
              to survive that state or the reader is stuck in it with no way
              back. Same rule that keeps the table mounted when a column funnel
              matches nothing.

              ⚠️ **`values` COMES FROM `selectedCycles`, NOT FROM
              `chosenCycleIds`.** The default is derived rather than stored, so
              reading the state directly would show an EMPTY control above two
              populated columns — the control disagreeing with the table about
              what is selected.
            */}
            <FilterSlot label="Cycles" className="w-72">
              <MultiCombobox
                values={selectedCycles.map((cycle) => String(cycle.id))}
                onChange={onCyclesChange}
                options={cycleOptions}
                placeholder="Choose cycles"
                searchPlaceholder="Filter cycles…"
                summaryNoun={{ singular: 'cycle', plural: 'cycles' }}
                /*
                 * Two chips is the default view and reads best spelled out;
                 * past three they wrap the bar into a wall. Which cycles are
                 * selected is never lost by counting them — they are the
                 * COLUMNS, named in the table's own headings.
                 */
                maxVisibleChips={3}
                /*
                 * ⛔ **THIS IS THE ONE MULTISELECT FILTER WITH NO `selectAll`,
                 * AND IT IS THE INTERLOCK RATHER THAN AN OVERSIGHT — do not
                 * "finish" the sweep by adding the prop here.** It would draw
                 * nothing: `MultiCombobox` suppresses the row beside ANY
                 * `footnote`, and this control carries one unconditionally.
                 *
                 * ⚠️ **AND THE INTERLOCK'S OWN PREMISE IS FALSE AT THIS ONE
                 * CALL SITE, WHICH IS WORTH KNOWING BEFORE SOMEBODY RELAXES
                 * IT.** It reads a footnote as a report of TRUNCATION, because
                 * every other one in the app is; the line below is a RULE about
                 * how the columns read, over a complete list of eight. So the
                 * suppression is right by accident, not by argument.
                 *
                 * It is left alone on the trade rather than on the reasoning:
                 * the alternatives are losing the sentence that says what the
                 * Change column subtracts, or giving the primitive a way to
                 * declare a footnote harmless — which is how an interlock
                 * guarding a customer-facing claim gets defeated the first time
                 * it is inconvenient. Ticking eight boxes is the cheaper cost.
                 */
                /* The rule, stated inside the control that sets it. */
                footnote="Columns read oldest to newest. Change and % change compare the newest two."
              />
            </FilterSlot>

            {/*
             * Only while there is a comparison: the empty state below has no
             * sections to arrange. `order-last` puts it after the bar's own
             * Filters disclosure, which carries the row's `ml-auto`.
             */}
            {current !== null && previous !== null && (
              <div className="order-last flex items-end">
                <LayoutToolbar layout={layout} />
              </div>
            )}
          </>
        }
        advanced={
          /*
            🔴 **THE ONE NARROWING A COLUMN HEADER CANNOT OWN.** Every other
            filter on this tab is a funnel on the column it acts on. This one
            tests a RULE ACROSS TWO COLUMNS — the larger of a company's two
            cycles — so it belongs to neither header and stays in the bar.

            ⚠️ **THE LABEL CARRIES THE RULE, because the rule is not the obvious
            one.** A company that used to pay 50,000 and now pays nothing has no
            later figure at all, so testing the later side alone would hide
            exactly the lost customers this tab exists to surface, and the
            shorter table would read as *"nobody has stopped"*.
          */
          /* ⚠️ **LABELLED `figuresCurrency`, NOT THE PICKER.** The threshold is
             compared against the amounts on screen, so a label taken from the
             request would read "GBP" over a comparison against dollars on any
             server predating `baseCurrency`. */
          <FilterSlot label={`Min. revenue, either cycle (${figuresCurrency})`} className="w-52">
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step={1000}
              placeholder="0"
              aria-label={`Minimum revenue in either cycle, in ${figuresCurrency}`}
              value={filters.minRevenue}
              onChange={(event) => setFilters({ minRevenue: event.target.value })}
            />
          </FilterSlot>
        }
        activeAdvanced={advancedCount}
        onClear={() => setFilters(EMPTY_MOVER_FILTERS)}
      />

      {/*
        🔴 **THE TILES AND THE TABLE ARE THE COMPARISON; THE FILTER BAR IS NOT.**
        Everything below needs two cycles, and fewer than two is a state the
        control above can reach — so the empty state replaces THIS and never the
        bar, or the reader is left holding a screen whose only way out has gone.
      */}
      {current === null || previous === null ? (
        <Panel className="min-w-0">
          <EmptyState title={noComparisonTitle} description={noComparisonDescription} />
        </Panel>
      ) : (
        <LayoutGrid
          layout={layout}
          render={{
            /*
             * The tiles and the conversion footnote under them move together:
             * the footnote qualifies the figures the tiles start.
             */
            summary: () => (
              <div className="flex min-w-0 flex-col gap-4">
          {/* ⚠️ Every figure in these tiles came through `revenueFor` against
           * `figuresCurrency`; the code beside them has to be the same one. */}
          <TopMoversSummary
            summaries={summaries}
            currency={figuresCurrency}
            active={activeOutcome}
            onSelect={selectOutcome}
          />

          {/*
            🔴 **HOW THESE FIGURES WERE ARRIVED AT.** Wording owned by
            `CONVERSION_APPROXIMATE_NOTE` in `@/lib/targets`; see it for the full
            reasoning. ⚠️ **That constant's own doc forbade this tab BY NAME until
            2026-09-18**, correctly, because this tab filtered and converted
            nothing — and the reversal is recorded there marked `SUPERSEDED`.

            ⚠️ **GATED ON `converted`, WHICH READS THE SERVER'S ECHO — never on a
            currency having been picked.** A disclaimer about a conversion that
            did not happen is a false statement in the reassuring direction.

            ⚠️ **HERE RATHER THAN BESIDE THE CHANGE COLUMN, and the movement
            sentence is the other way round.** This qualifies every FIGURE on the
            tab, so it sits under the tiles where the figures start; the sentence
            about a movement being partly the rate qualifies one SUBTRACTION, so
            it sits under the heading that names which two cycles were subtracted.
            Two facts, two neighbours.

            ⛔ **NOT HELPER TEXT, AND A SWEEP MUST NOT TAKE IT.** It states a fact
            about the data the reader cannot otherwise see, and is attached to a
            FIGURE rather than to a control.
          */}
          {/* ⚠️ **AND ON THERE BEING FIGURES.** The five tiles render whatever the
           * data, so a disclaimer over an empty comparison would qualify nothing.
           * `loading` is already handled by the skeleton above. */}
          {converted && rows.length > 0 && (
            <p className="m-0 text-2xs text-fg-subtle" title={CONVERSION_APPROXIMATE_NOTE.detail}>
              {CONVERSION_APPROXIMATE_NOTE.short}
            </p>
          )}
              </div>
            ),
            movers: () => (
          <Panel className="min-w-0">
            <PanelHeader
              /*
               * ⚠️ **THE TITLE IS THE COMPARISON, NEVER THE SPAN OF COLUMNS.**
               * With four cycles on screen a title reading "2022 - 23 → 2025 -
               * 26" would name a subtraction nothing performs. The arrow means
               * *compared with*, and it is true whatever else is shown.
               */
              title={`${previous.name} → ${current.name}`}
              description={
                <>
                  {caption}
                  {/*
                    🔴 **THE SENTENCE THE MULTI-SELECT MADE MANDATORY.** The
                    columns say which cycles are SHOWN; only this says which two
                    were SUBTRACTED. Without it a reader with three or more
                    columns assumes newest-versus-oldest — a wrong number
                    wearing a plausible label, which is the exact failure the
                    fixed comparison was introduced to prevent.

                    ⚠️ **Rendered at TWO cycles as well, where the title already
                    implies it.** A sentence that appears and disappears as the
                    selection crosses three is one a reader stops trusting, and
                    it is simply true in both cases.
                  */}
                  <span className="mt-0.5 block">
                    Change, % change and every outcome compare{' '}
                    <span className="font-medium text-fg-secondary">{previous.name}</span> with{' '}
                    <span className="font-medium text-fg-secondary">{current.name}</span> — the two
                    most recent cycles selected.
                    {selectedCycles.length > 2
                      ? ' Earlier columns are shown for context and are not part of that comparison, so a company with no revenue in either compared cycle is not listed.'
                      : ''}
                  </span>
                  {/*
                    🔴 **THE SENTENCE CONVERSION MADE MANDATORY, AND IT IS THIS
                    TAB'S ALONE.** Each cycle is priced at its own documents' bill
                    dates, so a company billed in dollars whose dollar revenue did
                    not move shows a change in pounds whenever the rate moved
                    between the two. **652 of 797 invoices are USD**, an ordinary
                    year-on-year move is a few per cent, and it can flip the
                    DIRECTION — three per cent more in dollars against a five per
                    cent adverse rate reads as `down`. Without this line the
                    Change column attributes a rate move to a customer, which is
                    exactly the class of false finding the currency-switch caveat
                    was built for and which conversion has otherwise closed.

                    ⛔ **IT CANNOT BE CORRECTED, ONLY SAID.** An FX-neutral change
                    needs the native amounts and the per-line rates, neither of
                    which reaches the browser on a converted response; deriving
                    one from a single rate would be client-side arithmetic across
                    units, and re-deriving any figure from one rate disagrees with
                    the server anyway. See `moverFor`.

                    ⛔ **NOT A WARNING STRIP, AND THAT IS THE DECISION RATHER THAN
                    A STYLE CHOICE.** It is true of EVERY converted view, because
                    nothing here can tell which companies were billed natively —
                    so as a strip it would be permanently on, sitting directly
                    above `partialCycleNote`, which is a real conditional alarm.
                    **An alarm wired to a normal condition destroys the alarm
                    rather than the condition**, and the one it would destroy is
                    the one measuring 22 Declining against 11. The quiet register
                    is what a permanent qualification earns.

                    ⚠️ **HERE RATHER THAN UNDER THE TILES**, where the conversion
                    disclaimer sits: this qualifies the SUBTRACTION, so it belongs
                    under the sentence that names which two cycles were
                    subtracted. A reader who reads what the Change column is also
                    reads what can be in it.

                    ⛔ **NOT HELPER TEXT.** It states a fact about the data nobody
                    can see from the screen — nothing distinguishes a movement in
                    trade from a movement in the rate — and the wrong conclusion
                    it prevents, *"this account is declining"*, is one somebody
                    acts on. Attached to FIGURES, not to a control.
                  */}
                  {converted && (
                    <span className="mt-0.5 block text-fg-subtle">
                      Each cycle is converted at its own documents’ dates, so part of a movement can
                      be the exchange rate rather than the customer.
                    </span>
                  )}
                </>
              }
              actions={
                <>
                  {/*
                    The mobile way in. The table scrolls sideways under a phone,
                    so the funnel on the last column is several swipes off
                    screen — this is the SAME state those headers spread, not a
                    second one. ⚠️ **`columnKeys`, so the cycle columns are in
                    it**: a fixed list would leave every cycle funnel
                    unreachable on a phone.
                  */}
                  <FilterSheet
                    keys={columnKeys}
                    headerFor={headerFor}
                    activeCount={activeCount}
                    clearAll={clearAll}
                    className="lg:hidden"
                  />
                  {/*
                    ⚠️ **Offered only while something is set**, so the control
                    doubles as a statement that something IS set — the same rule
                    the filter bar's own Clear follows.
                  */}
                  {narrowed && (
                    <Button size="sm" variant="secondary" onClick={clearEverything}>
                      Clear filters
                    </Button>
                  )}
                </>
              }
            />

            {/*
             * 🔴 **THE PARTIAL-CYCLE WARNING, AND IT IS THE PRICE OF THE
             * PICKER.** Absent on the default view, because both cycles there
             * have finished. Present the moment one has not — measured on
             * production, the running 2026 - 27 against a complete cycle gives
             * 22 Declining and 35 Stopped where two complete cycles give 11 and
             * 12, so twenty-three companies read as lost purely because they
             * have not been invoiced yet.
             *
             * ⚠️ **ABOVE the unmatched-lines note**, because it is about
             * whether the comparison is like-for-like at all, where that one is
             * about revenue missing from every column alike.
             */}
            {cycleNote !== null && (
              <Notice tone="warning">{cycleNote}</Notice>
            )}

            {/*
             * ⚠️ **Rendered whenever it is above zero.** It is the only thing that
             * can say the cycle columns do not sum to the business's revenue, and
             * a report that quietly omits a year is the kind of error found in
             * front of an accountant. **A WARNING, so the simplification pass left
             * it exactly as it was.**
             */}
            {unmatchedLines > 0 && (
              <Notice tone="warning">
                {pluralise(unmatchedLines, 'paid line')} could not be placed in any cycle — an
                absent or out-of-range bill date. That revenue is in none of these figures.
              </Notice>
            )}

            {/*
             * 🔴 **THE COUNT THAT KEEPS CONVERSION HONEST.** A paid line whose
             * own bill date has no stored exchange rate is left OUT of every
             * figure rather than priced at par — converting at 1.0 would put
             * dollars into a sterling total as though the two were the same
             * money. Without this the tab under-reports and looks complete, and
             * on THIS tab it also makes a movement that is partly an absence read
             * as a movement in trade.
             *
             * ⛔ **"COULD NOT BE CONVERTED", NEVER "IN ANOTHER CURRENCY".** A
             * foreign line IS converted now, so the old wording names a policy
             * that no longer exists and tells somebody nothing can be done; this
             * names missing reference data a person closes by backfilling the
             * rate for that date.
             *
             * ⚠️ **`null` AND `0` BOTH RENDER NOTHING, FOR DIFFERENT REASONS** —
             * nothing was examined, against nothing was lost. Tested explicitly
             * rather than through truthiness, so the distinction survives in the
             * code even though it does not survive on the screen.
             *
             * ⚠️ **BELOW the unmatched-lines note and ABOVE the table**, in the
             * same register: both say money is missing from every column alike,
             * and neither is about whether the comparison is like-for-like, which
             * is what `cycleNote` above them is for.
             */}
            {unpricedLines !== null && unpricedLines > 0 && (
              <Notice tone="warning">
                {pluralise(unpricedLines, 'paid line')} could not be converted into{' '}
                {figuresCurrency} — there is no exchange rate stored for{' '}
                {unpricedLines === 1 ? 'its' : 'their'} own bill date. That revenue is in none of
                these figures. The rate is missing, not the revenue.
              </Notice>
            )}

        {/*
         * 🔴 **AN EMPTY RESULT KEEPS THE TABLE WHEN A FILTER CAUSED IT, AND
         * REPLACES IT WHEN THE DATA DID.** The distinction is not cosmetic: the
         * filters live in the column headers now, so swapping the table for an
         * `EmptyState` after a funnel matched nothing would destroy the control
         * the reader had just used — and the popover they are still looking at
         * is portalled off a header that no longer exists. Measured by driving
         * it: the *Is empty* box on Billing region would not stay ticked,
         * because its own element was unmounted by the click that ticked it.
         *
         * With no filter set there is nothing to undo and no option list to
         * preserve — `allMovers` is empty, so every funnel would be empty too —
         * and the `EmptyState` can say the useful thing instead, which is that
         * this tab shows one currency at a time.
         */}
            {/*
             * ⚠️ *SUPERSEDED 2026-09-14, the clause in the middle of the
             * description: ", because no exchange rate is stored anywhere in
             * this system"*. `f_exchange_rates` exists (backend migration 178),
             * so that sentence told the reader the system lacked something it
             * has — an absence claim falsified by somebody building the thing.
             *
             * 🔴 **AND SUPERSEDED AGAIN 2026-09-18, BOTH HALVES, ON THE
             * CONVERTED PATH: "**No company was paid in USD in either compared
             * cycle** … *Movement is measured one currency at a time. Try EUR or
             * GBP.*"** Converted, the currency narrows nothing — so the title
             * blames a control that did not cause this, and the description is a
             * **dead instruction**: switching the currency restates the same
             * empty set in another unit. A dead instruction is worse than none,
             * because it reads as a way out and spends the reader's trust when it
             * does not work — the same correction `excludedByCurrency` on
             * `/targets/actuals` had to be made.
             *
             * ⚠️ **The unconverted branch is kept verbatim rather than dropped**:
             * against a server that does not convert, the currency really is the
             * one control whose effect a reader cannot see, and that sentence is
             * the only thing that says so.
             */}
            {sections.length === 0 && !narrowed ? (
              <EmptyState
                title={
                  converted
                    ? 'No company was paid in either compared cycle'
                    : `No company was paid in ${figuresCurrency} in either compared cycle`
                }
                description={
                  converted
                    ? /* A consequence, and a control that CAN change it — the
                       * cycles are the only thing left that could have emptied
                       * this, and they are a box the reader can tick right now. */
                      'Movement is measured between the two most recent cycles selected. Choose different cycles above to compare another pair.'
                    : `Movement is measured one currency at a time. Try ${REVENUE_CURRENCIES.filter((code) => code !== figuresCurrency).join(' or ')}.`
                }
              />
            ) : (
              <TopMoversTable
                sections={sections}
                summaries={summaries}
                /* ⚠️ The unit the figures are IN — every amount in this table
                 * came through `revenueFor` against this same value. */
                currency={figuresCurrency}
                cycles={selectedCycles}
                columnKeys={columnKeys}
                headerFor={headerFor}
                emptyMessage="No company matches these filters. Clear one above, or use Clear filters — the outcome tiles still count the whole comparison."
              />
            )}
          </Panel>
            ),
          }}
        />
      )}
    </div>
  );
}
